/**
 * PDF utilities — confirmation slips & transaction statements.
 * Uses jsPDF (+ autotable) which are already project dependencies.
 */
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COMPANY, KTT_EMAIL, KTT_CODE, formatUSD } from './keyGenerator';

const NAVY = [27, 42, 74];
const GOLD = [184, 134, 11];
const GREY = [100, 116, 139];
const TEAL = [13, 148, 136];
const CORAL = [231, 76, 111];

function ts() {
  return new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function header(doc, title, subtitle) {
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, 210, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(COMPANY, 14, 14);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(title, 14, 21);
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 225);
  doc.text(subtitle || ts(), 14, 26);
}

/**
 * Build a single-transaction confirmation slip PDF.
 * @param {object} slip - the slip object set by send pages
 * @returns {jsPDF}
 */
export function buildSlipDoc(slip) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  header(doc, 'Transaction Confirmation Slip', ts());

  const rows = [
    ['Method', slip.method || '-'],
    ['Transaction Reference', slip.txnRef || '-'],
    ['Value Date', slip.valueDate || '-'],
    ['Beneficiary', slip.beneficiary || '-'],
    ['Account No.', slip.accountNo || '-'],
    ['Receiver Bank', slip.receiverBank || slip.bankName || '-'],
    ['Currency', slip.currency || 'USD'],
  ];
  if (slip.keyTest) rows.push(['Key Test No.', slip.keyTest]);
  if (slip.kttEmail) rows.push(['KTT Email', slip.kttEmail]);
  if (slip.kttCode) rows.push(['Sender KTT Code', slip.kttCode]);
  if (slip.receiverKTTCode) rows.push(['Receiver KTT Code', slip.receiverKTTCode]);
  if (slip.receiverBIC) rows.push(['Receiver BIC/SWIFT', slip.receiverBIC]);
  if (slip.apiEndpoint) rows.push(['API Endpoint', slip.apiEndpoint]);
  rows.push(['Narrative', slip.narrative || '-']);

  autoTable(doc, {
    startY: 38,
    head: [['Field', 'Detail']],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9, halign: 'left' },
    bodyStyles: { fontSize: 10, textColor: [30, 41, 59] },
    columnStyles: { 0: { cellWidth: 60, textColor: GREY, fontStyle: 'bold' }, 1: { cellWidth: 122 } },
    margin: { left: 14, right: 14 },
  });

  let y = doc.lastAutoTable.finalY + 8;

  // Amount band
  doc.setFillColor(...NAVY);
  doc.rect(14, y, 182, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('AMOUNT', 18, y + 9);
  const amt = `${slip.currency || 'USD'} ${parseFloat(slip.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  doc.setFontSize(13);
  doc.text(amt, 192, y + 9, { align: 'right' });
  y += 22;

  // Security key band
  if (slip.keyTest) {
    doc.setDrawColor(...GOLD);
    doc.setFillColor(248, 246, 240);
    doc.roundedRect(14, y, 182, 20, 2, 2, 'FD');
    doc.setTextColor(...GREY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('SECURITY KEY TEST', 105, y + 7, { align: 'center' });
    doc.setTextColor(...GOLD);
    doc.setFontSize(20);
    doc.text(String(slip.keyTest), 105, y + 16, { align: 'center' });
    y += 28;
  }

  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('This is a system-generated confirmation slip. No signature required.', 105, y, { align: 'center' });
  doc.text(`${COMPANY}  —  ${KTT_EMAIL}  —  KTT ${KTT_CODE}`, 105, y + 5, { align: 'center' });

  return doc;
}

/** Download a confirmation slip PDF. */
export function downloadSlipPDF(slip) {
  const doc = buildSlipDoc(slip);
  const ref = (slip.txnRef || 'SLIP').replace(/[^\w]/g, '-');
  doc.save(`KTT_Slip_${ref}.pdf`);
}

/**
 * Download a full statement-of-transactions PDF (history).
 * @param {Array} transactions
 * @param {object} wallet
 * @param {object} [opts] - { from, to, title }
 */
export function downloadStatementPDF(transactions, wallet, opts = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const title = opts.title || 'Statement of Transactions';
  // landscape header
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, 297, 28, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(COMPANY, 14, 13);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(title, 14, 20);
  doc.setFontSize(8);
  doc.setTextColor(200, 210, 225);
  const range = (opts.from || opts.to) ? `Period: ${opts.from || '—'} to ${opts.to || '—'}` : `Generated: ${ts()}`;
  doc.text(range, 14, 25);

  let totalCredit = 0, totalDebit = 0;
  const body = transactions.map(tx => {
    totalCredit += tx.creditAmount || 0;
    totalDebit += tx.debitAmount || 0;
    return [
      tx.id || tx.ref || '',
      (tx.date || '').slice(0, 10),
      tx.mt || tx.type || '',
      (tx.description || '').slice(0, 48),
      tx.counterparty || tx.beneficiary || '',
      tx.keyTest || '',
      tx.creditAmount ? formatUSD(tx.creditAmount) : '',
      tx.debitAmount ? formatUSD(tx.debitAmount) : '',
      tx.status || '',
    ];
  });

  autoTable(doc, {
    startY: 34,
    head: [['Reference', 'Date', 'Type', 'Description', 'Counterparty', 'Key Test', 'Credit', 'Debit', 'Status']],
    body,
    theme: 'striped',
    headStyles: { fillColor: NAVY, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 7.5, textColor: [30, 41, 59] },
    columnStyles: {
      0: { cellWidth: 42, font: 'courier', fontSize: 7 },
      5: { textColor: GOLD, fontStyle: 'bold' },
      6: { textColor: TEAL, halign: 'right' },
      7: { textColor: CORAL, halign: 'right' },
    },
    margin: { left: 14, right: 14 },
  });

  let y = doc.lastAutoTable.finalY + 8;
  doc.setDrawColor(...NAVY);
  doc.setLineWidth(0.5);
  doc.line(14, y, 283, y);
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...TEAL);
  doc.text(`Total Credit: ${formatUSD(totalCredit)}`, 14, y);
  doc.setTextColor(...CORAL);
  doc.text(`Total Debit: ${formatUSD(totalDebit)}`, 90, y);
  doc.setTextColor(...NAVY);
  doc.text(`Net Balance: ${formatUSD(wallet?.balance ?? (totalCredit - totalDebit))}`, 166, y);
  doc.setTextColor(...GREY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(`${transactions.length} transaction(s)  —  ${COMPANY}  —  ${KTT_EMAIL}`, 14, y + 6);

  doc.save(`KTT_Statement_${new Date().toISOString().slice(0, 10)}.pdf`);
}
